import { useEffect, useRef, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import logo from '../../assets/logo1.png';
import appConfig from '../../config/appConfig';
import appRoutes from '../../config/routes';
import { api, apiRoutes } from '../../api';
import authContent from '../../content/fa/auth.json';
import legalContent from '../../content/fa/legal.json';
import './AuthForm.css';

const { brand, heading, labels, placeholders, errors, professionalLinks } = authContent;
const registrationTerms = legalContent.registration;

const EyeIcon = ({ crossed = false }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M2.4 12s3.5-6 9.6-6 9.6 6 9.6 6-3.5 6-9.6 6-9.6-6-9.6-6Z" />
    <circle cx="12" cy="12" r="2.7" />
    {crossed && <path d="m4 4 16 16" />}
  </svg>
);

function AuthForm() {
  const location = useLocation();
  const [isLogin, setIsLogin] = useState(() => location.pathname !== '/register');
  const [formData, setFormData] = useState({
    username_or_email: '',
    email: '',
    phone: '',
    password: '',
    is_agent: false,
    is_developer: false,
    national_id: '',
    agree_to_terms: false,
    assistant_name: ''
  });
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const termsCloseButtonRef = useRef(null);

  useEffect(() => {
    if (!showTerms) return undefined;

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setShowTerms(false);
    };

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);
    termsCloseButtonRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showTerms]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const fetchDeveloperMembership = async () => {
    try {
      const response = await api.get(apiRoutes.developers.mine);
      const developers = Array.isArray(response.data) ? response.data : response.data.results || [];
      const developer = developers[0];
      return developer
        ? {
            is_developer: true,
            developer_id: developer.id,
            developer_name: developer.display_name || developer.legal_name || ''
          }
        : { is_developer: false };
    } catch {
      return { is_developer: false };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isLogin && formData.password.length < 8) {
      setError(errors.password);
      return;
    }
    if (!isLogin && !formData.phone) {
      setError(errors.phone);
      return;
    }
    if (!isLogin && !formData.agree_to_terms) {
      setError(errors.agreement);
      return;
    }
    setIsSubmitting(true);
    try {
      let response;
      if (isLogin) {
        response = await api.post(apiRoutes.auth.token, {
          username: formData.username_or_email,
          password: formData.password,
        });

        // Store tokens and username
        localStorage.setItem(appConfig.auth.accessTokenKey, response.data.access);
        localStorage.setItem(appConfig.auth.refreshTokenKey, response.data.refresh);
        localStorage.setItem('username', formData.username_or_email);

        // Get user details
        const userResponse = await api.get(apiRoutes.auth.user, {
          headers: { Authorization: `Bearer ${response.data.access}` }
        });

        // Store user ID from token payload
        try {
          const tokenPayload = JSON.parse(atob(response.data.access.split('.')[1]));
          if (tokenPayload.user_id) {
            localStorage.setItem('user_id', tokenPayload.user_id.toString());
          }
        } catch (error) {

        }

        // Store user profile data
        const developerProfile = await fetchDeveloperMembership();
        localStorage.setItem('user_profile', JSON.stringify({
          email: userResponse.data.email,
          first_name: userResponse.data.first_name || '',
          last_name: userResponse.data.last_name || '',
          phone: userResponse.data.phone || '',
          address: userResponse.data.address || '',
          city: userResponse.data.city || '',
          province: userResponse.data.province || '',
          postal_code: userResponse.data.postal_code || '',
          is_agent: userResponse.data.is_agent || false,
          is_gis_team: userResponse.data.is_gis_team || false,
          groups: userResponse.data.groups || [],
          ...developerProfile,
        }));
      } else {
        const registerPayload = {
          phone: formData.phone,
          password: formData.password,
          agree_to_terms: formData.agree_to_terms,
        };

        if (formData.email && formData.email.trim()) {
          registerPayload.email = formData.email;
        }

        if (formData.assistant_name && formData.assistant_name.trim()) {
          registerPayload.assistant_name = formData.assistant_name.trim();
        }

        response = await api.post(apiRoutes.auth.register, registerPayload);

        // Store tokens and user info
        localStorage.setItem(appConfig.auth.accessTokenKey, response.data.access);
        localStorage.setItem(appConfig.auth.refreshTokenKey, response.data.refresh);
        localStorage.setItem('username', formData.email || formData.phone);

        // Store user ID from registration response
        if (response.data.user_id) {
          localStorage.setItem('user_id', response.data.user_id.toString());
        }

        // Get and store user profile data
        const userResponse = await api.get(apiRoutes.auth.user, {
          headers: { Authorization: `Bearer ${response.data.access}` }
        });

        const developerProfile = await fetchDeveloperMembership();
        localStorage.setItem('user_profile', JSON.stringify({
          email: userResponse.data.email,
          first_name: userResponse.data.first_name || '',
          last_name: userResponse.data.last_name || '',
          phone: userResponse.data.phone || '',
          address: userResponse.data.address || '',
          city: userResponse.data.city || '',
          province: userResponse.data.province || '',
          postal_code: userResponse.data.postal_code || '',
          is_agent: userResponse.data.is_agent || false,
          is_gis_team: userResponse.data.is_gis_team || false,
          groups: userResponse.data.groups || [],
          ...developerProfile,
          is_developer: formData.is_developer || developerProfile.is_developer,
        }));
      }

      setError('');
      navigate(!isLogin && formData.is_developer ? appRoutes.developerRegister : appRoutes.main);
    } catch (err) {
      if (err.response?.data) {
        const errorData = err.response.data;
        if (typeof errorData === 'string') {
          setError(errorData);
        } else if (errorData.detail) {
          setError(errorData.detail);
        } else if (errorData.email) {
          setError(`ایمیل: ${errorData.email[0]}`);
        } else if (errorData.phone) {
          setError(`شماره همراه: ${errorData.phone[0]}`);
        } else if (errorData.password) {
          setError(`رمز عبور: ${errorData.password[0]}`);
        } else {
          setError(JSON.stringify(errorData));
        }
      } else {
        setError(isLogin ? errors.login : errors.register);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="auth-page">
      <section className="auth-visual" aria-label="نمایی از تهران و خانه‌ای مدرن">
        <img className="auth-visual-image" src="/image2.jpg" alt="" aria-hidden="true" />
        <div className="auth-visual-shade" />
        <div className="auth-visual-content" dir="rtl">
          <Link to="/" className="auth-visual-logo-link" aria-label="رفتن به صفحه اصلی سایتا">
            <img src={logo} alt="لوگوی سایتا" className="auth-visual-logo" />
          </Link>
          <span className="auth-handwritten">{brand.tagline}</span>
          <div className="auth-title-rule" />
          <h1>{brand.headlineBefore}<br /><em>{brand.headlineEmphasis}</em></h1>
          <div className="auth-title-rule auth-title-rule-short" />
          <p>{brand.promise.split('\n').map((line, index) => <span key={line}>{index > 0 && <br />}{line}</span>)}</p>
        </div>
        <div className="auth-benefits" dir="rtl" aria-label="مزیت‌های سایتا">
          <div>
            <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5" /><path d="m15.5 15.5 5 5" /></svg>
            <span>{brand.benefits[0]}</span>
          </div>
          <div>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m8.5 12.5 2.3 2.3 5-5.6" /><path d="M12 2.7 20 6v5.3c0 4.8-3.4 8.3-8 10-4.6-1.7-8-5.2-8-10V6l8-3.3Z" /></svg>
            <span>{brand.benefits[1]}</span>
          </div>
          <div>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 20V10m5 10V5m6 15v-7m5 7V3" /></svg>
            <span>{brand.benefits[2]}</span>
          </div>
          <div>
            <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="8" cy="8" r="3" /><circle cx="17" cy="7" r="2.5" /><path d="M2.5 20v-2.2c0-3 2.4-5.3 5.5-5.3s5.5 2.3 5.5 5.3V20M14 13c.8-.7 1.8-1 3-1 2.5 0 4.5 1.8 4.5 4.2V18" /></svg>
            <span>{brand.benefits[3]}</span>
          </div>
        </div>
      </section>

      <section className="auth-form-section">
        <div className="auth-form-shell" dir="rtl">
          <Link to="/" className="auth-logo-link" aria-label="رفتن به صفحه اصلی سایتا">
            <img src={logo} alt="لوگوی سایتا" className="auth-logo" />
          </Link>

          <div className="auth-heading">
            <span className="auth-heading-kicker">{heading.kicker}</span>
            <h2>{isLogin ? heading.loginTitle : heading.registerTitle}</h2>
            <p>{isLogin ? heading.loginDescription : heading.registerDescription}</p>
          </div>

          <div className="auth-mode-tabs" role="tablist" aria-label="ورود یا ثبت‌نام">
            <button type="button" role="tab" aria-selected={isLogin} className={isLogin ? 'active' : ''} onClick={() => { setIsLogin(true); setError(''); }}>{labels.login}</button>
            <button type="button" role="tab" aria-selected={!isLogin} className={!isLogin ? 'active' : ''} onClick={() => { setIsLogin(false); setError(''); }}>{labels.register}</button>
          </div>

          {error && <div className="auth-error" role="alert"><span>!</span>{error}</div>}

          <form onSubmit={handleSubmit} className="auth-form">
            {isLogin ? (
              <div className="auth-field">
                <label htmlFor="auth-identity">{labels.usernameOrEmail}</label>
                <input
                  id="auth-identity"
                  type="text"
                  name="username_or_email"
                  value={formData.username_or_email}
                  onChange={handleChange}
                  placeholder={placeholders.usernameOrEmail}
                  autoComplete="username"
                  required
                />
              </div>
            ) : (
              <>
                <div className="auth-field">
                  <label htmlFor="auth-phone">{labels.phone}</label>
                  <input
                    id="auth-phone"
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder={placeholders.phone}
                    autoComplete="tel"
                    inputMode="tel"
                    required
                  />
                </div>
                <div className="auth-field">
                  <label htmlFor="auth-email">{labels.email} <span>{labels.optional}</span></label>
                  <input
                    id="auth-email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={placeholders.email}
                    autoComplete="email"
                    dir="ltr"
                  />
                </div>
              </>
            )}
            <div className="auth-field">
              <label htmlFor="auth-password">{labels.password}</label>
              <div className="auth-password-wrap">
                <input
                  id="auth-password"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder={isLogin ? placeholders.currentPassword : placeholders.newPassword}
                  autoComplete={isLogin ? 'current-password' : 'new-password'}
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(prev => !prev)}
                  aria-label={showPassword ? 'پنهان کردن رمز عبور' : 'نمایش رمز عبور'}
                  title={showPassword ? 'پنهان کردن رمز عبور' : 'نمایش رمز عبور'}
                  className="auth-password-toggle"
                >
                  <EyeIcon crossed={showPassword} />
                </button>
              </div>
            </div>
            {isLogin && (
              <div className="auth-forgot-row">
                <span>{labels.forgotPasswordQuestion}</span>
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                >
                  {labels.forgotPasswordAction}
                </button>
              </div>
            )}
            {!isLogin && (
              <div className="auth-field">
                <label htmlFor="auth-assistant">{labels.assistantName} <span>{labels.optional}</span></label>
                <input
                  id="auth-assistant"
                  type="text"
                  name="assistant_name"
                  value={formData.assistant_name}
                  onChange={handleChange}
                  maxLength={50}
                  placeholder={placeholders.assistantName}
                />
              </div>
            )}
            {!isLogin && (
              <div className="auth-checks">
                <label>
                  <input
                    type="checkbox"
                    name="is_developer"
                    checked={formData.is_developer}
                    onChange={handleChange}
                  />
                  {labels.developerSignup}
                </label>
                <div className="auth-terms-check">
                  <input
                    id="auth-agree-terms"
                    type="checkbox"
                    name="agree_to_terms"
                    checked={formData.agree_to_terms}
                    onChange={handleChange}
                  />
                  <span>
                    <label htmlFor="auth-agree-terms">{registrationTerms.agreementBefore} </label>
                    <button
                      type="button"
                      className="auth-terms-link"
                      onClick={() => setShowTerms(true)}
                    >
                      {registrationTerms.title}
                    </button>
                    <label htmlFor="auth-agree-terms"> {registrationTerms.agreementAfter}</label>
                  </span>
                </div>
              </div>
            )}
            <button
              type="submit"
              className="auth-submit"
              disabled={isSubmitting}
            >
              <span>{isSubmitting ? labels.submitting : (isLogin ? labels.login : labels.register)}</span>
              {!isSubmitting && <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m14 7-5 5 5 5" /></svg>}
            </button>
          </form>

          <p className="auth-switch-copy">
            {isLogin ? labels.noAccount : labels.haveAccount}
            <button
              onClick={() => {
                setIsLogin(!isLogin);
                setError('');
                setFormData({ username_or_email: '', email: '', phone: '', password: '', is_agent: false, is_developer: false, national_id: '', agree_to_terms: false, assistant_name: '' });
              }}
            >
              {isLogin ? labels.registerHere : labels.loginHere}
            </button>
          </p>

          <div className="auth-professional-links">
            <Link to="/agent-application">
              <span>{professionalLinks.agentDescription}</span><b>{professionalLinks.agentTitle}</b>
            </Link>
            <Link to="/developer-register">
              <span>{professionalLinks.developerDescription}</span><b>{professionalLinks.developerTitle}</b>
            </Link>
          </div>

          <p className="auth-copyright">
            {authContent.termsNoticeBefore}{' '}
            <button type="button" onClick={() => setShowTerms(true)}>{registrationTerms.linkLabel}</button>
            {' '}{authContent.termsNoticeAfter}
          </p>
        </div>
      </section>

      {showTerms && (
        <div
          className="auth-terms-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setShowTerms(false);
          }}
        >
          <section
            className="auth-terms-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="auth-terms-title"
            aria-describedby="auth-terms-intro"
            dir="rtl"
          >
            <header className="auth-terms-header">
              <div>
                <span>{registrationTerms.brand}</span>
                <h2 id="auth-terms-title">{registrationTerms.title}</h2>
              </div>
              <button
                ref={termsCloseButtonRef}
                type="button"
                className="auth-terms-close"
                onClick={() => setShowTerms(false)}
                aria-label="بستن قوانین و مقررات"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" /></svg>
              </button>
            </header>

            <div className="auth-terms-body">
              <p id="auth-terms-intro" className="auth-terms-intro">
                {registrationTerms.intro}
              </p>
              {registrationTerms.sections.map((section) => (
                <article key={section.number}>
                  <span>{section.number}</span>
                  <div>
                    <h3>{section.title}</h3>
                    <p>{section.body}</p>
                  </div>
                </article>
              ))}
            </div>

            <footer className="auth-terms-footer">
              <button type="button" className="auth-terms-secondary" onClick={() => setShowTerms(false)}>{registrationTerms.closeLabel}</button>
              {!isLogin && (
                <button
                  type="button"
                  className="auth-terms-accept"
                  onClick={() => {
                    setFormData((current) => ({ ...current, agree_to_terms: true }));
                    setShowTerms(false);
                  }}
                >
                  {registrationTerms.acceptLabel}
                </button>
              )}
            </footer>
          </section>
        </div>
      )}
    </main>
  );
}

export default AuthForm;
